import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LoginFormData, RegisterFormData, ChangePasswordFormData, User } from '@/types';
import { authService } from '@/services/api';
import { useErrorHandler } from '@/hooks/useErrorHandler';

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginFormData) => Promise<void>;
  register: (data: RegisterFormData) => Promise<void>;
  logout: () => void;
  changePassword: (data: ChangePasswordFormData) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { handleError, handleSuccess } = useErrorHandler();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      setToken(storedToken);
    }
    
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Erro ao parsear dados do usuário:', error);
        localStorage.removeItem('user');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      const response = await authService.login(data.email, data.senha);
      
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      handleSuccess('Login realizado com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao fazer login');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      await authService.register(data.nome, data.email, data.senha);
      handleSuccess('Usuário criado com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao criar usuário');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    handleSuccess('Logout realizado com sucesso!');
  };

  const changePassword = async (data: ChangePasswordFormData) => {
    if (!token || !user) throw new Error('Usuário não autenticado');
    
    try {
      setIsLoading(true);
      await authService.changePassword(user.id, data.senhaAtual, data.novaSenha);
      handleSuccess('Senha alterada com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao alterar senha');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    token,
    user,
    isAuthenticated: !!token,
    isLoading,
    login,
    register,
    logout,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
